import React, { useRef, useState } from 'react';
import CkEditorField from '../../shared/CkEditorField';
import FileManager from './FileManager';
import RulesCard, { labelFor } from './RulesCard';
import FileBadge from './FileBadge';

// Doc default for chapter/forum events (mirrors the shell-creation default).
const DEFAULT_RULES = { view: 'members_and_registrants', download: 'members_and_registrants' };

/**
 * Event article manager island (chairs/admins only — Blade gates the mount).
 * Zero full-page reloads: create, save, uploads, publish/revert and delete are
 * all AJAX. On page load an existing article opens in PREVIEW (draft included);
 * right after "Add article" it opens in EDIT, since that click means "write".
 *
 * Payload contract (ForumController::eventArticlePayload):
 * { article: null|{status, body, files[]}, licenseKey, csrf, urls{...} }
 */

const jsonHeaders = (csrf) => ({
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-CSRF-TOKEN': csrf,
    'X-Requested-With': 'XMLHttpRequest',
});

function ConfirmModal({ open, title, confirmLabel, danger, busy, onConfirm, onCancel, children }) {
    if (!open) return null;

    return (
        <div className="ea-modal" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
            <div className="ea-modal__card">
                <h3 className="ea-modal__title">{title}</h3>
                <div className="ea-modal__body">{children}</div>
                <div className="ea-modal__actions">
                    <button type="button" className="back-btn" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button
                        type="button"
                        className={danger ? 'ea-btn-danger' : 'ea-btn-publish'}
                        onClick={onConfirm}
                        disabled={busy}
                    >
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function EventArticleManager({ payload }) {
    const { licenseKey, csrf, urls } = payload;

    const [exists, setExists] = useState(!!payload.article);
    const [status, setStatus] = useState(payload.article ? Number(payload.article.status) : 0);
    const [files, setFiles] = useState(payload.article ? payload.article.files || [] : []);
    const [rules, setRules] = useState((payload.article && payload.article.rules) || DEFAULT_RULES);
    const [rulesSavedAt, setRulesSavedAt] = useState(null);
    const [lastEdit, setLastEdit] = useState({
        by: (payload.article && payload.article.updated_by_name) || null,
        at: (payload.article && payload.article.updated_at) || null,
    });

    // Mutating endpoints return the authoritative stamp — displaying that (and
    // never the client clock) keeps no-op saves from drifting the line.
    const applyLastEdit = (json) => {
        if (json && json.updated_at) {
            setLastEdit({ by: json.updated_by_name || null, at: json.updated_at });
        }
    };

    const formatEditTime = (iso) => (iso
        ? new Date(iso).toLocaleString([], { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '');
    const [mode, setMode] = useState('preview'); // page load always lands on preview
    // Editor (and its CDN assets) only boots on the first entry into edit mode:
    // initializing inside the hidden container measures zero width, and preview
    // visitors shouldn't pay for CKEditor at all.
    const [editorBooted, setEditorBooted] = useState(false);
    const [previewHtml, setPreviewHtml] = useState(payload.article ? payload.article.body || '' : '');
    const bodyRef = useRef(payload.article ? payload.article.body || '' : '');

    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [flash, setFlash] = useState(null); // {type: 'error'|'ok', text}
    const [publishModal, setPublishModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [modalBusy, setModalBusy] = useState(false);
    // Generic confirmation gate: {title, body, label, danger, run} | null.
    const [confirmAction, setConfirmAction] = useState(null);

    const isPublished = status === 1;

    // On a published article, incremental edits (save/upload/rules) ask for
    // confirmation before running; drafts stay friction-free.
    const guardPublished = (label, run) => {
        if (!isPublished) {
            run();
            return;
        }
        setConfirmAction({
            title: 'This material is published',
            body: 'These changes will be visible to readers immediately.',
            label,
            run,
        });
    };

    // File removal always confirms — it permanently deletes from storage.
    const confirmRemoveFile = (filename, run) => {
        setConfirmAction({
            title: 'Remove this file?',
            body: `"${filename}" will be permanently deleted from storage${isPublished ? ' and will disappear from the page immediately' : ''}.`,
            label: 'Remove',
            danger: true,
            run,
        });
    };

    const post = async (url, body, method = 'POST') => {
        const response = await fetch(url, { method, headers: jsonHeaders(csrf), body: JSON.stringify(body ?? {}) });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
    };

    /* ── create (AJAX — no reload; lands in edit mode to start writing) ── */
    const createArticle = async () => {
        setCreating(true);
        setFlash(null);
        try {
            const { article } = await post(urls.store, {});
            bodyRef.current = article.body || '';
            setStatus(Number(article.status));
            setFiles(article.files || []);
            setRules(article.rules || DEFAULT_RULES);
            setLastEdit({ by: article.updated_by_name || null, at: article.updated_at || null });
            setPreviewHtml(article.body || '');
            setExists(true);
            setMode('edit');
            setEditorBooted(true);
        } catch (error) {
            setFlash({ type: 'error', text: 'Could not create the material. Please try again.' });
        } finally {
            setCreating(false);
        }
    };

    /* ── body save ── */
    const saveBody = async () => {
        setSaving(true);
        setFlash(null);
        try {
            const json = await post(urls.save, { content: bodyRef.current });
            setSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            applyLastEdit(json);
        } catch (error) {
            setFlash({ type: 'error', text: 'Could not save the material. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    /* ── rules (auto-save on change) ── */
    const saveRules = async (next) => {
        setRules(next);
        setFlash(null);
        try {
            const json = await post(urls.rules, next);
            setRulesSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            applyLastEdit(json);
        } catch (error) {
            setFlash({ type: 'error', text: 'Could not save the access rules. Please try again.' });
        }
    };

    /* ── publish / revert (publish saves the latest edits + rules first) ── */
    const setArticleStatus = async (next) => {
        setModalBusy(true);
        setFlash(null);
        try {
            if (next === 1) {
                await post(urls.save, { content: bodyRef.current });
                await post(urls.rules, rules);
            }
            const json = await post(urls.status, { status: next });
            setStatus(next);
            setPublishModal(false);
            applyLastEdit(json);

            if (next === 1) {
                // Publishing lands on the preview — you see exactly what went
                // live. Reverting stays in edit mode (you revert to keep working).
                setPreviewHtml(bodyRef.current);
                setMode('preview');
            }
        } catch (error) {
            setFlash({ type: 'error', text: 'Could not update the material status. Please try again.' });
        } finally {
            setModalBusy(false);
        }
    };

    /* ── delete (AJAX — island resets to the empty state) ── */
    const deleteArticle = async () => {
        setModalBusy(true);
        try {
            await post(urls.destroy, {}, 'DELETE');
            setDeleteModal(false);
            setExists(false);
            setStatus(0);
            setFiles([]);
            setRules(DEFAULT_RULES);
            setRulesSavedAt(null);
            setLastEdit({ by: null, at: null });
            setPreviewHtml('');
            bodyRef.current = '';
            setMode('preview');
        } catch (error) {
            setFlash({ type: 'error', text: 'Could not delete the material. Please try again.' });
        } finally {
            setModalBusy(false);
        }
    };

    /* ── preview download: form POST so the attachment downloads in place ── */
    const downloadFile = (filename) => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = urls.download;
        form.style.display = 'none';
        [['_token', csrf], ['filename', filename]].forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        form.remove();
    };

    const togglePreview = () => {
        if (mode === 'edit') {
            setPreviewHtml(bodyRef.current);
            setMode('preview');
        } else {
            setMode('edit');
            setEditorBooted(true);
        }
    };

    /* ── empty state ── */
    if (!exists) {
        return (
            <section className="lk-section ea-section">
                <h2 className="ea-section-title">Event Material</h2>
                <div className="ea-empty">
                    <p className="ea-empty__title">This event has no material yet.</p>
                    <p className="ea-empty__hint">
                        It will be created as a draft — invisible to everyone except chairs and admins
                        until you publish it.
                    </p>
                    <button type="button" className="sp-ocard-btn" onClick={createArticle} disabled={creating}>
                        {creating ? 'Creating…' : '＋ Add material'}
                    </button>
                    {flash && <p className="ea-flash-error">{flash.text}</p>}
                </div>
            </section>
        );
    }

    return (
        <section className={`lk-section ea-section${mode === 'preview' && files.length === 0 ? ' ea-section--solo' : ''}`}>
            <h2 className="ea-section-title">
                {'Event Material '}
                <span className={`ea-badge ${isPublished ? 'ea-badge--published' : 'ea-badge--draft'}`}>
                    {isPublished ? 'Published' : 'Draft'}
                </span>
                {!isPublished && (
                    <span className="ea-badge-note">Only chairs &amp; admins can see this section.</span>
                )}
                <span className="ea-title-spacer" />
                <button type="button" className="back-btn ea-mode-btn" onClick={togglePreview}>
                    {mode === 'preview' ? '✎ Edit material' : '👁 Preview'}
                </button>
            </h2>

            {/* ── PREVIEW: same main/aside structure as About + Takeaways. The grid
                   columns stay fixed even without files, so the body keeps the
                   main-column width instead of stretching across the page. ── */}
            {mode === 'preview' && (
                <div className="event-grid">
                    <div className="event-grid__main">
                        <div className="ea-card">
                            {previewHtml
                                ? <div className="ea-read ck-content" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                                : <p className="ea-read-empty">No content yet — switch to edit mode to start writing.</p>}
                        </div>
                    </div>

                    {files.length > 0 && (
                        <aside className="event-grid__aside">
                            <div className="ea-card">
                                <h4 className="ea-block-title ea-block-title--primary">Attachments</h4>
                                <ul className="ea-file-list">
                                    {files.map((file) => (
                                        <li key={file.filename} className="ea-file-row">
                                            <FileBadge filename={file.filename} />
                                            <span className="ea-file-main">
                                                <span className="ea-file-name">{file.filename}</span>
                                            </span>
                                            <button type="button" className="back-btn ea-dl-btn" onClick={() => downloadFile(file.filename)}>
                                                ⬇ Download
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </aside>
                    )}
                </div>
            )}

            {/* ── EDIT (kept mounted so the editor and its undo history survive toggling).
                   Same main/aside grid as the preview, so files stay in the same spot
                   when toggling modes; rules span the full width below. ── */}
            <div style={{ display: mode === 'edit' ? 'block' : 'none' }}>
                <div className="event-grid">
                    <div className="event-grid__main">
                        <div className="ea-card">
                            <h4 className="ea-block-title"><span className="ea-step">1</span> Event Content</h4>
                            <p className="ea-block-hint">Rich text editor for recap, takeaways, prep notes, links, and follow-up instructions.</p>
                            {editorBooted && (
                                <CkEditorField
                                    value={bodyRef.current}
                                    preset={payload.isAdmin ? 'content' : 'content-chair'}
                                    licenseKey={licenseKey}
                                    uploadUrl={urls.uploadImage}
                                    uploadHeaders={{ 'X-CSRF-TOKEN': csrf }}
                                    maxWidth=""
                                    onChange={(html) => { bodyRef.current = html; }}
                                />
                            )}
                            <div className="ea-save-row">
                                <button type="button" className="back-btn" onClick={() => guardPublished('Save', saveBody)} disabled={saving}>
                                    {saving ? 'Saving…' : 'Save'}
                                </button>
                                {savedAt && <span className="ea-save-flash">Saved ✓ {savedAt}</span>}
                            </div>
                        </div>
                    </div>

                    <aside className="event-grid__aside">
                        <div className="ea-card">
                            <h4 className="ea-block-title"><span className="ea-step">2</span> Attachments</h4>
                            <p className="ea-block-hint">Upload slides, recap PDFs, templates, worksheets, or other supporting files.</p>
                            <FileManager
                                files={files}
                                setFiles={setFiles}
                                onEdited={applyLastEdit}
                                confirmGate={(run) => guardPublished('Upload', run)}
                                confirmRemove={confirmRemoveFile}
                                urls={urls}
                                csrf={csrf}
                            />
                        </div>
                    </aside>
                </div>

                <div className="ea-card">
                    <h4 className="ea-block-title"><span className="ea-step">3</span> Visibility</h4>
                    <p className="ea-block-hint">Who can see this page and download its attachments once published. Saved automatically.</p>
                    <RulesCard
                        rules={rules}
                        onChange={(next) => guardPublished('Apply', () => saveRules(next))}
                        chapterName={payload.chapterName}
                        savedAt={rulesSavedAt}
                    />
                </div>
            </div>

            {flash && <p className="ea-flash-error">{flash.text}</p>}

            {/* ── footer: edit mode only — page load (preview) stays action-free ── */}
            {mode === 'edit' && (
                <div className="ea-footer">
                    {payload.canDelete && (
                        <button type="button" className="ea-btn-danger-ghost" onClick={() => setDeleteModal(true)}>
                            🗑 Delete material
                        </button>
                    )}
                    {lastEdit.by && (
                        <span className="ea-lastedit">
                            Last edit by <strong>{lastEdit.by}</strong> at <strong>{formatEditTime(lastEdit.at)}</strong>
                        </span>
                    )}
                    {!isPublished && (
                        <button type="button" className="ea-btn-publish" onClick={() => setPublishModal(true)}>
                            Publish material
                        </button>
                    )}
                    {isPublished && (
                        <button
                            type="button"
                            className="back-btn"
                            onClick={() => setConfirmAction({
                                title: 'Revert to draft?',
                                body: 'The material will be hidden from readers immediately — only chairs & admins will see it until it is published again.',
                                label: 'Revert',
                                run: () => setArticleStatus(0),
                            })}
                        >
                            Revert to draft
                        </button>
                    )}
                </div>
            )}

            <ConfirmModal
                open={!!confirmAction}
                title={confirmAction ? confirmAction.title : ''}
                confirmLabel={confirmAction ? confirmAction.label : ''}
                danger={confirmAction ? !!confirmAction.danger : false}
                busy={false}
                onConfirm={() => {
                    const action = confirmAction;
                    setConfirmAction(null);
                    if (action) action.run();
                }}
                onCancel={() => setConfirmAction(null)}
            >
                <p>{confirmAction ? confirmAction.body : ''}</p>
            </ConfirmModal>

            <ConfirmModal
                open={publishModal}
                title="Publish this material?"
                confirmLabel="Publish"
                busy={modalBusy}
                onConfirm={() => setArticleStatus(1)}
                onCancel={() => setPublishModal(false)}
            >
                <p>Your latest edits and access rules will be saved and published.</p>
                <p className="ea-modal__hint">
                    👁 Visible to: <strong>{labelFor(rules.view)}</strong>
                    <br />
                    ⬇ Downloads: <strong>{labelFor(rules.download)}</strong>
                </p>
            </ConfirmModal>

            <ConfirmModal
                open={deleteModal}
                title="Delete this material?"
                confirmLabel="Delete material"
                danger
                busy={modalBusy}
                onConfirm={deleteArticle}
                onCancel={() => setDeleteModal(false)}
            >
                <p>
                    This <strong>permanently</strong> deletes the material body and{' '}
                    <strong>{files.length} file(s)</strong> from storage. This cannot be undone.
                </p>
            </ConfirmModal>
        </section>
    );
}
